/**
 * Consent Settings Modal
 * 
 * Allows users to granularly control their cookie preferences.
 * Shows detailed information about each category.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useConsent } from '../../contexts/ConsentContext';
import { CONSENT_CATEGORIES, ConsentState, CategoryConfig } from '../../lib/consent';
import { X, Cookie, Check, Shield, BarChart3, Megaphone, ChevronDown, ChevronUp } from 'lucide-react';

const CATEGORY_ICONS: Record<string, React.ElementType> = {
  necessary: Shield,
  analytics: BarChart3,
  marketing: Megaphone,
};

interface CategoryToggleProps {
  category: CategoryConfig;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
}

function CategoryToggle({ category, enabled, onToggle }: CategoryToggleProps) {
  const { t } = useTranslation('common');
  const [isExpanded, setIsExpanded] = useState(false);
  const Icon = CATEGORY_ICONS[category.id] || Cookie;

  return (
    <div className="consent-category border border-border rounded-lg overflow-hidden">
      {/* Category Header */}
      <div className="consent-category-header flex items-center justify-between p-4 bg-card">
        <div className="consent-category-info flex items-center gap-3 flex-1">
          <div className={`consent-category-icon w-10 h-10 rounded-full flex items-center justify-center ${
            enabled ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
          }`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="consent-category-text flex-1">
            <div className="consent-category-name-row flex items-center gap-2">
              <h4 className="consent-category-name text-sm font-semibold text-foreground">
                {t(`consent.categories.${category.id}.name`, category.name)}
              </h4>
              {category.required && (
                <span className="consent-category-required text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded">
                  {t('consent.modal.alwaysOn', 'Always On')}
                </span>
              )}
            </div>
            <p className="consent-category-desc text-xs text-muted-foreground mt-0.5 line-clamp-2">
              {t(`consent.categories.${category.id}.description`, category.description)}
            </p>
          </div>
        </div>

        {/* Toggle Switch */}
        <div className="consent-category-toggle-area flex items-center gap-3">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="consent-category-expand-btn p-1 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
          
          <label className="consent-toggle-label relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => onToggle(e.target.checked)}
              disabled={category.required}
              className="sr-only peer"
            />
            <div className={`consent-toggle-track w-11 h-6 rounded-full peer transition-colors ${
              category.required 
                ? 'bg-primary/50 cursor-not-allowed' 
                : 'bg-muted peer-checked:bg-primary peer-focus:ring-2 peer-focus:ring-primary/20'
            }`}>
              <div className={`consent-toggle-thumb absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                enabled ? 'translate-x-5' : 'translate-x-0'
              }`}>
                {enabled && <Check className="w-3 h-3 text-primary absolute top-1 left-1" />}
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="consent-category-details p-4 pt-0 bg-muted/30 border-t border-border">
          <div className="consent-category-purposes mt-3">
            <h5 className="consent-purposes-title text-xs font-semibold text-foreground uppercase tracking-wide mb-2">
              {t('consent.modal.purposes', 'Purposes')}
            </h5>
            <ul className="consent-purposes-list space-y-1">
              {category.purposes.map((purpose, index) => (
                <li key={index} className="consent-purpose-item text-xs text-muted-foreground flex items-start gap-2">
                  <span className="consent-purpose-bullet w-1 h-1 rounded-full bg-muted-foreground mt-1.5 flex-shrink-0" />
                  {purpose}
                </li>
              ))}
            </ul>
          </div>

          <div className="consent-category-recipients mt-3">
            <h5 className="consent-recipients-title text-xs font-semibold text-foreground uppercase tracking-wide mb-2">
              {t('consent.modal.dataRecipients', 'Data Recipients')}
            </h5>
            <ul className="consent-recipients-list space-y-1">
              {category.dataRecipients.map((recipient, index) => (
                <li key={index} className="consent-recipient-item text-xs text-muted-foreground flex items-start gap-2">
                  <span className="consent-recipient-bullet w-1 h-1 rounded-full bg-muted-foreground mt-1.5 flex-shrink-0" />
                  {recipient}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export function ConsentModal() {
  const { t } = useTranslation('common');
  const { isModalOpen, closeModal, consentState, updateConsent, acceptAll, rejectAll } = useConsent();
  
  // Local state for pending changes
  const [localState, setLocalState] = useState<ConsentState>(consentState);

  // Sync local state with context when modal opens
  useEffect(() => {
    if (isModalOpen) {
      setLocalState(consentState);
    }
  }, [isModalOpen, consentState]);

  // Handle escape key
  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape' && isModalOpen) {
        closeModal();
      }
    }
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isModalOpen, closeModal]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isModalOpen]);

  if (!isModalOpen) {
    return null;
  }

  const handleCategoryToggle = (categoryId: string, enabled: boolean) => {
    setLocalState(prev => ({
      ...prev,
      [categoryId]: enabled,
    }));
  };

  const handleSave = async () => {
    await updateConsent(localState);
  };

  const handleAcceptAll = async () => {
    await acceptAll();
  };

  const handleRejectAll = async () => {
    await rejectAll();
  };

  return (
    <div className="consent-modal-overlay fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4">
      <div 
        className="consent-modal-backdrop absolute inset-0"
        onClick={closeModal}
        aria-hidden="true"
      />
      
      <div className="consent-modal bg-card rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden relative z-10 flex flex-col">
        {/* Header */}
        <div className="consent-modal-header flex items-center justify-between p-4 border-b border-border">
          <div className="consent-modal-title-area flex items-center gap-3">
            <div className="consent-modal-icon w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Cookie className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="consent-modal-title text-lg font-semibold text-foreground">
                {t('consent.modal.title', 'Cookie Preferences')}
              </h2>
              <p className="consent-modal-subtitle text-xs text-muted-foreground">
                {t('consent.modal.subtitle', 'Manage your cookie settings')}
              </p>
            </div>
          </div>
          <button
            onClick={closeModal}
            className="consent-modal-close p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="consent-modal-content flex-1 overflow-y-auto p-4 space-y-3">
          <p className="consent-modal-description text-sm text-muted-foreground mb-4">
            {t('consent.modal.description', 'We use cookies and similar technologies to improve your experience. You can choose which categories of cookies you want to allow. Your preferences will be saved and you can change them at any time.')}
          </p>

          {CONSENT_CATEGORIES.map((category) => (
            <CategoryToggle
              key={category.id}
              category={category}
              enabled={localState[category.id]}
              onToggle={(enabled) => handleCategoryToggle(category.id, enabled)}
            />
          ))}

          {/* Policy Links */}
          <div className="consent-modal-links flex gap-4 pt-2">
            <a 
              href="/legal/cookies/" 
              className="consent-policy-link text-xs text-primary hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('consent.modal.cookiePolicy', 'Cookie Policy')}
            </a>
            <a 
              href="/legal/privacy/" 
              className="consent-policy-link text-xs text-primary hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('consent.modal.privacyPolicy', 'Privacy Policy')}
            </a>
          </div>
        </div>

        {/* Footer */}
        <div className="consent-modal-footer flex flex-col sm:flex-row gap-2 p-4 border-t border-border bg-muted/30">
          <button
            onClick={handleRejectAll}
            className="consent-modal-reject-btn flex-1 px-4 py-2.5 text-sm font-medium text-foreground bg-secondary border border-border rounded-md hover:bg-muted transition-colors"
          >
            {t('consent.modal.rejectAll', 'Reject All')}
          </button>
          <button
            onClick={handleSave}
            className="consent-modal-save-btn flex-1 px-4 py-2.5 text-sm font-medium text-foreground bg-secondary border border-border rounded-md hover:bg-muted transition-colors"
          >
            {t('consent.modal.savePreferences', 'Save Preferences')}
          </button>
          <button
            onClick={handleAcceptAll}
            className="consent-modal-accept-btn flex-1 px-4 py-2.5 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 transition-colors"
          >
            {t('consent.modal.acceptAll', 'Accept All')}
          </button>
        </div>
      </div>
    </div>
  );
}

